/**
 * Rahmen: Navigation links, Kopfzeile mit Modus-Badges je Integration
 * (MOCK/LIVE, Ruling R2) und Umgebung (TEST/LIVE), Inhalt rechts.
 * Zusätzlich das Badge „SPA-MOCK“, sobald die Mock-Schicht der SPA Antworten
 * liefert (api/mock/index.ts) – damit niemand Mock-Daten für echte hält.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ROLLEN_RANG, type SessionUser } from '@storeos/contracts';
import { logout, systemStatus } from '../api';
import { gemockt, MOCK_EVENT, mockModus } from '../api/mock';
import { sichtbareScreens } from '../screens';

const NAME: Record<string, string> = { woo: 'Woo', dhl: 'DHL', shipcloud: 'Shipcloud', buchhaltung: 'Buchhaltung', mail: 'Mail', printnode: 'PrintNode' };

function useSpaMock() {
  const [anzahl, setAnzahl] = useState(gemockt.size);
  useEffect(() => {
    const h = () => setAnzahl(gemockt.size);
    window.addEventListener(MOCK_EVENT, h);
    return () => window.removeEventListener(MOCK_EVENT, h);
  }, []);
  return mockModus() === 'erzwungen' ? 'alle' : anzahl > 0 ? anzahl : null;
}

export function Shell({ user, onLogout, children }: { user: SessionUser; onLogout: () => void; children: ReactNode }) {
  const status = useQuery({ queryKey: ['system-status'], queryFn: systemStatus, refetchInterval: 60_000 });
  const sichtbar = sichtbareScreens(user.rolle, (r) => ROLLEN_RANG[r]);
  const spaMock = useSpaMock();
  const [navOffen, setNavOffen] = useState(false);

  return (
    <div className={`shell${navOffen ? ' nav-offen' : ''}`}>
      <aside className="nav">
        <div className="marke">StoreOS <span>Nuts &amp; Golden</span></div>
        <nav aria-label="Hauptnavigation" onClick={() => setNavOffen(false)}>
          {sichtbar.map((s) => (
            <NavLink key={s.pfad} to={s.pfad} end={s.pfad === '/' || s.pfad === '/produktion'} className={({ isActive }) => (isActive ? 'aktiv' : '')}>
              {s.name}
            </NavLink>
          ))}
        </nav>
        <div className="benutzer">
          <div className="benutzer-name">{user.name}</div>
          <div className="rolle" data-testid="rolle">{user.rolle}</div>
          <button type="button" className="knopf-nav" onClick={() => logout().then(onLogout)}>Abmelden</button>
        </div>
      </aside>
      <div className="inhalt">
        <header className="kopf" data-testid="kopfzeile">
          <button type="button" className="nav-schalter" aria-label="Navigation" onClick={() => setNavOffen((o) => !o)}>Menü</button>
          {status.data ? (
            <>
              <span className={`badge env-${status.data.env}`} data-testid="badge-env" title={status.data.env === 'live' ? 'STOREOS_ENV=live: echte Belege' : 'STOREOS_ENV ≠ live: Belege tragen TEST-, nichts ist echt'}>
                {status.data.env === 'live' ? 'LIVE' : 'TEST'}
              </span>
              {status.data.integrationen.map((i) => (
                <span key={i.name} className={`badge modus-${i.mode}`} title={i.hinweis} data-testid="badge-integration">
                  {NAME[i.name] ?? i.name} {i.mode === 'fehler' ? 'FEHLER' : i.mode === 'aus' ? 'AUS' : i.mode === 'mock' ? 'MOCK' : 'LIVE'}
                </span>
              ))}
              <span className="badge neutral" title="Rückschreibungen nach Woo (Status / Notiz / Bestand). Aus = Trockenlauf mit Protokoll.">
                Woo-Schreiben {(['status', 'notiz', 'bestand'] as const).map((k) => (status.data!.wooSchreiben[k] ? 'AN' : 'aus')).join(' / ')}
              </span>
              <span className="badge neutral" title="Datenbank">{status.data.db.dialekt}</span>
            </>
          ) : (
            <span className="badge neutral">{status.isError ? 'Status nicht erreichbar' : 'Status …'}</span>
          )}
          {spaMock && (
            <span className="badge spa-mock" data-testid="badge-spa-mock" data-endpunkte={JSON.stringify([...gemockt])}
              title={spaMock === 'alle' ? 'VITE_API_MOCK=1: Alle Daten kommen aus der Mock-Schicht der SPA, keine API.' : `${spaMock} Endpunkt(e) fehlen in der API und kommen aus der Mock-Schicht der SPA: ${[...gemockt].join(', ')}`}>
              SPA-MOCK{spaMock !== 'alle' ? ` (${spaMock})` : ''}
            </span>
          )}
        </header>
        <main>{children}</main>
      </div>
    </div>
  );
}
